<template>
  <!-- 创作弹窗 -->
  <BasePopup
    :visible="visible"
    popup-class="create-popup"
    :show-title-icon="true"
    close-btn-class="popup-content-close"
    @close="handleClose"
  >
    <template #title>
      <div class="popup-title-text-label">{{ langData.createPopupTitle }}</div>
    </template>
    <div class="select-box">
      <select v-model="localCreateType" required>
        <option :value="''" disabled hidden>{{ langData.selectType }}</option>
        <option value="5">{{ langData.createType5 }}</option>
        <option value="6">{{ langData.createType6 }}</option>
        <option value="7">{{ langData.createType7 }}</option>
      </select>
    </div>
    <input
      class="title-input"
      type="text"
      v-model="localCreateTitle"
      :placeholder="langData.inputTitle || '请输入标题'"
      maxlength="20"
    />
    <textarea
      class="text-input"
      v-model="localCreateContent"
      :placeholder="langData.inputContent || '请输入内容'"
      maxlength="200"
    ></textarea>
    <div class="image-upload-area">
      <div class="upload-title">
        {{ langData.uploadImage || "上传图片" }}
        <span class="upload-title-text">
          ({{ localCreateImages.filter((image) => image.url).length }}/3)
          {{ langData.uploadImageTip || "最多3张" }}
        </span>
      </div>
      <div class="image-list">
        <!-- 使用 vuedraggable 实现拖拽排序 -->
        <draggable
          v-model="localCreateImages"
          :disabled="false"
          ghost-class="ghost"
          chosen-class="chosen"
          drag-class="drag"
          :animation="200"
          item-key="id"
          class="draggable-container"
          handle=".drag-handle"
          @start="onDragStart"
          @end="onDragEnd"
        >
          <template #item="{ element: img, index }">
            <div class="draggable-item" :class="{ 'has-image': img.url }">
              <div
                class="add-image-box"
                @click="openImageUpload(index)"
                v-if="img.url === ''"
              >
                <div class="add-icon">+</div>
                <div class="add-text">
                  {{ langData.addImage || "添加图片" }}
                </div>
              </div>
              <div class="view-image-box" v-else>
                <!-- 拖拽手柄图标 -->
                <div
                  class="drag-handle"
                  v-if="uploadingIndex !== index"
                  title="拖动排序"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="2"
                      y="4"
                      width="12"
                      height="1.5"
                      rx="0.75"
                      fill="currentColor"
                    />
                    <rect
                      x="2"
                      y="7.25"
                      width="12"
                      height="1.5"
                      rx="0.75"
                      fill="currentColor"
                    />
                    <rect
                      x="2"
                      y="10.5"
                      width="12"
                      height="1.5"
                      rx="0.75"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div
                  class="image-item"
                  :style="{ backgroundImage: `url(${img.url})` }"
                >
                  <!-- 上传进度遮罩 -->
                  <div
                    class="upload-progress-overlay"
                    v-if="uploadingIndex === index"
                  >
                    <div class="upload-progress-bar">
                      <div
                        class="progress-fill"
                        :style="{ width: uploadProgress + '%' }"
                      ></div>
                    </div>
                    <div class="upload-progress-text">
                      {{ uploadProgress }}%
                    </div>
                  </div>
                </div>
                <div
                  class="remove-btn"
                  @click.stop="removeImage(index)"
                  v-if="uploadingIndex !== index"
                ></div>
              </div>
            </div>
          </template>
        </draggable>
      </div>
      <input
        type="file"
        ref="fileInput"
        style="display: none"
        accept="image/jpeg,image/png,image/jpg"
        @change="handleImageChange"
      />
      <div class="checked-container" @click="handleCheckboxClick">
        <div
          class="check-icon"
          :class="{
            checked: localChecked,
            error: showCheckError && !localChecked,
          }"
        >
          <div class="checked" v-show="localChecked"></div>
        </div>
        {{ langData.createPopupTip }}
      </div>
    </div>

    <template #footer>
      <div class="submit-msg-toast" :class="{ show: showingText }">
        {{ showingText }}
      </div>
      <div class="submit-btn" @click="handleSubmit">{{ langData.submitBtn }}</div>
      <div class="cancel-btn" @click="handleClose">{{ langData.cancelBtn }}</div>
    </template>
  </BasePopup>

  <!-- 保存确认弹窗 -->
  <SaveConfirmPopup
    :visible="showSaveConfirm"
    @update:visible="showSaveConfirm = $event"
    @confirm="confirmSave"
    @cancel="cancelSave"
  />
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, computed, inject } from "vue";
import draggable from "vuedraggable";
import LanguageHook from "@hooks/language";
import BasePopup from "./BasePopup.vue";
import SaveConfirmPopup from "./SaveConfirmPopup.vue";
import { getJSON, getActivityApi } from "@/utils";

// 语言相关
const { langData } = LanguageHook();

// 注入提示消息方法
const showTipMessage = inject("showTipMessage", (msg) => {
  console.warn("showTipMessage not provided, fallback to alert:", msg);
  alert(msg);
});

// 定义props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  createType: {
    type: String,
    default: "",
  },
  createTitle: {
    type: String,
    default: "",
  },
  createContent: {
    type: String,
    default: "",
  },
  createImages: {
    type: Array,
    default: () => [
      { url: "", file: null, id: "img-0" },
      { url: "", file: null, id: "img-1" },
      { url: "", file: null, id: "img-2" },
    ],
  },
  showingText: {
    type: String,
    default: "",
  },
});

// 定义emits
const emit = defineEmits([
  "close",
  "submit",
  "update:createType",
  "update:createTitle",
  "update:createContent",
  "update:createImages",
]);

// 本地状态
const localCreateType = ref("4");
const localCreateTitle = ref("");
const localCreateContent = ref("");
const localCreateImages = ref([
  { url: "", file: null, id: "img-0" },
  { url: "", file: null, id: "img-1" },
  { url: "", file: null, id: "img-2" },
]);
const fileInput = ref(null);
const localChecked = ref(false);
const isDragging = ref(false);
const showCheckError = ref(false); // 是否显示勾选错误提示
const uploadingIndex = ref(-1); // 当前正在上传的图片索引
const uploadProgress = ref(0); // 上传进度
const showSaveConfirm = ref(false); // 是否显示保存确认弹窗

// 计算属性：是否有多张图片（用于显示拖拽手柄）
const hasMultipleImages = computed(() => {
  return localCreateImages.value.filter((img) => img.url).length > 1;
});

// 监听弹窗打开状态，加载缓存数据
watch(
  () => props.visible,
  (newVal, oldVal) => {
    if (newVal && !oldVal) {
      // 弹窗从关闭变为打开时，加载 localStorage 数据
      loadFromLocalStorage();
    }
  }
);

// 监听props变化，同步到本地状态
watch(
  () => props.createType,
  (newVal) => {
    if (localCreateType.value !== newVal) {
      localCreateType.value = newVal;
    }
  },
  { immediate: true }
);

watch(
  () => props.createTitle,
  (newVal) => {
    if (localCreateTitle.value !== newVal) {
      localCreateTitle.value = newVal;
    }
  },
  { immediate: true }
);

watch(
  () => props.createContent,
  (newVal) => {
    if (localCreateContent.value !== newVal) {
      localCreateContent.value = newVal;
    }
  },
  { immediate: true }
);

// 监听 props.createImages 变化，同步到本地状态（单向数据流）
// 优化：移除深度监听和 JSON.stringify，只在必要时更新
watch(
  () => props.createImages,
  (newVal) => {
    // 确保只使用前3项，并确保每个项都有id
    const validImages = newVal.slice(0, 3).map((img, index) => ({
      ...img,
      id: img.id || `img-${index}`,
    }));

    // 确保始终有3个位置
    while (validImages.length < 3) {
      const index = validImages.length;
      validImages.push({
        url: "",
        file: null,
        uploadedUrl: "",
        id: `img-${index}`,
      });
    }

    localCreateImages.value = validImages;
  },
  { immediate: true }
);

// 监听本地状态变化，同步到父组件
watch(localCreateType, (newVal, oldVal) => {
  if (newVal !== oldVal && newVal !== props.createType) {
    emit("update:createType", newVal);
  }
});

watch(localCreateTitle, (newVal, oldVal) => {
  if (newVal !== oldVal && newVal !== props.createTitle) {
    emit("update:createTitle", newVal);
  }
});

watch(localCreateContent, (newVal, oldVal) => {
  if (newVal !== oldVal && newVal !== props.createContent) {
    emit("update:createContent", newVal);
  }
});

// 移除 localCreateImages 的深度 watch
// 优化：改为在具体操作函数中直接 emit，避免深度监听的性能消耗

// 保存数据到 localStorage
const saveToLocalStorage = () => {
  const dataToSave = {
    type: localCreateType.value,
    title: localCreateTitle.value,
    content: localCreateContent.value,
    // 确保只保存前3项
    images: localCreateImages.value.slice(0, 3).map((img) => ({
      url: img.url,
      uploadedUrl: img.uploadedUrl || "",
    })),
  };

  // 只有当有数据时才保存
  if (
    dataToSave.type ||
    dataToSave.title ||
    dataToSave.content ||
    dataToSave.images.some((img) => img.url)
  ) {
    localStorage.setItem("rog_gongchuangye_data", JSON.stringify(dataToSave));
  }
};

// 从 localStorage 加载数据
const loadFromLocalStorage = () => {
  try {
    const savedData = localStorage.getItem("rog_gongchuangye_data");
    if (savedData) {
      const data = JSON.parse(savedData);

      localCreateType.value = data.type || "";
      localCreateTitle.value = data.title || "";
      localCreateContent.value = data.content || "";

      // 恢复图片数据
      if (data.images && Array.isArray(data.images)) {
        // 限制最多只取前3项
        const imagesToLoad = data.images.slice(0, 3);

        localCreateImages.value = imagesToLoad.map((img, index) => ({
          url: img.url || "",
          uploadedUrl: img.uploadedUrl || "",
          file: null,
          id: `img-${index}`,
        }));

        // 确保始终有3个位置
        while (localCreateImages.value.length < 3) {
          const index = localCreateImages.value.length;
          localCreateImages.value.push({
            url: "",
            file: null,
            uploadedUrl: "",
            id: `img-${index}`,
          });
        }
      }
    }
  } catch (error) {
    console.error("加载本地数据失败:", error);
  }
};

// 清除 localStorage 数据
const clearLocalStorage = () => {
  localStorage.removeItem("rog_gongchuangye_data");
};

// 检查是否有未保存的内容
const hasUnsavedContent = () => {
  return (
    localCreateType.value ||
    localCreateTitle.value.trim() ||
    localCreateContent.value.trim() ||
    localCreateImages.value.some((img) => img.url)
  );
};

// 关闭弹窗
const handleClose = () => {
  // 检查是否有未保存的内容
  if (hasUnsavedContent()) {
    // 显示保存确认弹窗
    showSaveConfirm.value = true;
  } else {
    // 没有内容，直接关闭
    emit("close");
  }
};

// 确认保存并退出
const confirmSave = () => {
  // 保存当前数据到 localStorage
  saveToLocalStorage();
  showSaveConfirm.value = false;
  emit("close");
};

// 取消保存，直接退出（不保存内容）
const cancelSave = () => {
  // 不保存，清空当前填写的数据
  localCreateType.value = "";
  localCreateTitle.value = "";
  localCreateContent.value = "";
  localCreateImages.value = [
    { url: "", file: null, id: "img-0" },
    { url: "", file: null, id: "img-1" },
    { url: "", file: null, id: "img-2" },
  ];
  clearLocalStorage();
  showSaveConfirm.value = false;
  emit("close");
};

// 提交创作
const handleSubmit = () => {
  // 检查创作类型是否为空
  if (!localCreateType.value) {
    showTipMessage(langData.selectType || "请选择创作类型");
    return;
  }

  // 检查标题是否为空
  if (!localCreateTitle.value.trim()) {
    showTipMessage(langData.inputTitle || "请输入标题");
    return;
  }

  // 检查是否勾选授权条款
  if (!localChecked.value) {
    showCheckError.value = true;
    showTipMessage(langData.checkTermsTip);
    return;
  }

  // 确保所有图片都已上传完成
  if (uploadingIndex.value !== -1) {
    showTipMessage(langData.imageUploadingTip);
    return;
  }

  // 过滤出有效的图片，优先使用服务器返回的 URL
  const validImages = localCreateImages.value
    .filter((img) => img.url || img.uploadedUrl)
    .map((img) => ({
      url: img.uploadedUrl || img.url,
      file: img.file,
    }));

  // 类型5（美照分享）、6（我的波利）、7（职业分享）必须上传图片
  if (
    (["5", "6", "7"].includes(localCreateType.value)) &&
    validImages.length === 0
  ) {
    showTipMessage(langData.imageRequired);
    return;
  }

  // 提交成功后清除 localStorage 缓存
  clearLocalStorage();

  emit("submit", {
    type: localCreateType.value,
    title: localCreateTitle.value,
    content: localCreateContent.value,
    images: validImages,
  });
};

// 打开图片上传
const openImageUpload = (index) => {
  if (fileInput.value) {
    fileInput.value.click();
  }
};

// 处理图片变化
const handleImageChange = async (event) => {
  const files = event.target.files;
  if (!files.length) return;

  const file = files[0];

  // 检查文件大小（10MB）
  if (file.size > 10 * 1024 * 1024) {
    showTipMessage(langData.imageSizeTip);
    event.target.value = "";
    return;
  }

  // 检查文件类型
  if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
    showTipMessage(langData.imageFormatTip);
    event.target.value = "";
    return;
  }

  // 找到第一个空的位置
  const emptyIndex = localCreateImages.value.findIndex((img) => !img.url);
  if (emptyIndex === -1) {
    showTipMessage(langData.imageMaxTip);
    event.target.value = "";
    return;
  }

  // 先读取图片预览
  const reader = new FileReader();
  reader.onload = (e) => {
    localCreateImages.value[emptyIndex].url = e.target.result;
    localCreateImages.value[emptyIndex].file = file;
    // 预览加载完成后同步到父组件
    emit("update:createImages", [...localCreateImages.value]);
  };
  reader.readAsDataURL(file);

  // 开始分片上传
  try {
    const uploadedUrl = await uploadImageInChunks(file, emptyIndex);

    // 上传成功后，更新为服务器返回的 URL
    localCreateImages.value[emptyIndex].url = uploadedUrl;
    localCreateImages.value[emptyIndex].uploadedUrl = uploadedUrl; // 保存服务器 URL
    localCreateImages.value[emptyIndex].file = file;
    // 上传成功后同步到父组件
    emit("update:createImages", [...localCreateImages.value]);
  } catch (error) {
    showTipMessage(langData.imageUploadFailTip);
    // 上传失败，清除预览
    localCreateImages.value[emptyIndex].url = "";
    localCreateImages.value[emptyIndex].file = null;
    // 上传失败后同步到父组件
    emit("update:createImages", [...localCreateImages.value]);
  }

  // 重置文件输入
  event.target.value = "";
};

// 移除图片
const removeImage = (index) => {
  localCreateImages.value[index] = {
    url: "",
    file: null,
    uploadedUrl: "",
    id: `img-${index}`,
  };
  // 直接 emit 同步到父组件，避免依赖深度 watch
  emit("update:createImages", [...localCreateImages.value]);
};

// 拖拽开始
const onDragStart = (evt) => {
  isDragging.value = true;
};

// 拖拽结束
const onDragEnd = (evt) => {
  isDragging.value = false;

  // 确保拖拽后数组长度始终为3（防止拖拽bug导致多项）
  if (localCreateImages.value.length > 3) {
    localCreateImages.value = localCreateImages.value.slice(0, 3);
  } else if (localCreateImages.value.length < 3) {
    while (localCreateImages.value.length < 3) {
      const index = localCreateImages.value.length;
      localCreateImages.value.push({
        url: "",
        file: null,
        uploadedUrl: "",
        id: `img-${index}`,
      });
    }
  }

  // 拖拽完成后直接 emit 同步到父组件
  emit("update:createImages", [...localCreateImages.value]);
};

// 分片上传图片
const uploadImageInChunks = async (file, index) => {
  try {
    uploadingIndex.value = index;
    uploadProgress.value = 0;

    // 分片大小 (5MB)
    const chunkSize = 5 * 1024 * 1024;
    const chunks = Math.ceil(file.size / chunkSize);

    // 获取文件扩展名
    const fileExtension = file.name.split(".").pop().toLowerCase();
    const picType = `.${fileExtension}`;

    // 1. 获取分片上传地址
    const uploadResponse = await getJSON(
      getActivityApi(window.platform_id, window.activity_name, "upload"),
      {
        params: JSON.stringify({
          slice: chunks,
          picType: picType,
        }),
      }
    );

    if (+uploadResponse.result !== 1) {
      throw new Error(uploadResponse.msg || "获取上传地址失败");
    }

    const { urls, uploadId } = uploadResponse.data;

    // 2. 分片上传到各个 URL
    const uploadPromises = [];
    for (let i = 0; i < chunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const uploadPromise = fetch(urls[i], {
        method: "PUT",
        body: chunk,
        headers: {
          "Content-Type": file.type,
        },
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`上传分片 ${i + 1} 失败`);
        }
        // 更新进度
        uploadProgress.value = Math.round(((i + 1) / chunks) * 100);
        return response;
      });

      uploadPromises.push(uploadPromise);
    }

    await Promise.all(uploadPromises);

    // 3. 完成分片上传
    const completeResponse = await getJSON(
      getActivityApi(window.platform_id, window.activity_name, "complete"),
      {
        params: JSON.stringify({
          uploadId: uploadId,
        }),
      }
    );

    if (+completeResponse.result !== 1) {
      throw new Error(completeResponse.msg || "完成上传失败");
    }

    // 上传成功，从 complete 接口返回的数据中获取最终 URL
    const finalUrl = completeResponse.data?.url || urls[0].split("?")[0];

    uploadingIndex.value = -1;
    uploadProgress.value = 0;

    return finalUrl;
  } catch (error) {
    uploadingIndex.value = -1;
    uploadProgress.value = 0;
    console.error("分片上传失败:", error);
    throw error;
  }
};

// 处理勾选框点击
const handleCheckboxClick = () => {
  localChecked.value = !localChecked.value;
  // 勾选后清除错误提示
  if (localChecked.value) {
    showCheckError.value = false;
  }
};
</script>

<style scoped>
/* 拖拽相关的额外样式 */
.draggable-container {
  min-height: 149px; /* 确保容器有最小高度 */
}

.draggable-item {
  /* 确保拖拽项目有正确的显示 */
  display: inline-block;
}

/* 拖拽时的全局样式 */
:global(.sortable-ghost) {
  opacity: 0.4;
}

:global(.sortable-chosen) {
  cursor: grabbing !important;
}

/* 防止拖拽时选中文本 */
.image-list {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* 确保 view-image-box 相对定位 */
.view-image-box {
  position: relative;
}

/* 当正在拖拽时，隐藏其他交互元素 */
.draggable-item.chosen .remove-btn {
  pointer-events: none;
  opacity: 0.5;
}

/* 上传进度遮罩 */
.upload-progress-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  z-index: 10;
}

.upload-progress-bar {
  width: 80%;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50 0%, #8bc34a 100%);
  transition: width 0.3s ease;
  border-radius: 4px;
}

.upload-progress-text {
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
}

/* 提交状态 Toast */
.submit-msg-toast {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 15px 30px;
  background-color: rgba(0, 0, 0, 0.8);
  color: #ffffff;
  font-size: 18px;
  border-radius: 8px;
  white-space: nowrap;
  z-index: 9999;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  pointer-events: none;
}

.submit-msg-toast.show {
  opacity: 1;
  visibility: visible;
}
</style>
